/**
 * AddProductToBranchModal
 * Allows branch admins to add products from the global catalog to their branch inventory.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Package, Plus, Check, AlertCircle, Loader2 } from 'lucide-react';
import { getProducts } from '../../api/products';
import { initializeBranchInventory, getBranchInventory } from '../../api/branchInventory';

const AddProductToBranchModal = ({ branchId, branchName, onClose, onSuccess }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [existingProductIds, setExistingProductIds] = useState(new Set());
  // Changed to object: { productId: quantity }
  const [selectedProducts, setSelectedProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Load existing branch products to filter them out
  const loadExistingProducts = useCallback(async () => {
    try {
      const response = await getBranchInventory(branchId, { limit: 1000 });
      if (response.success) {
        const ids = new Set((response.data || []).map(item => item.product_id));
        setExistingProductIds(ids);
      }
    } catch (err) {
      console.error('Error loading existing products:', err);
    }
  }, [branchId]);

  // Load global products
  const loadProducts = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      const currentPage = reset ? 1 : page;
      
      const response = await getProducts({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        isActive: true
      });
      
      if (response.success) {
        const newProducts = response.products || [];
        // Filter out products already in branch
        const filteredProducts = newProducts.filter(
          p => !existingProductIds.has(p.id)
        );
        
        if (reset) {
          setProducts(filteredProducts);
        } else {
          setProducts(prev => [...prev, ...filteredProducts]);
        }
        
        setHasMore(response.pagination?.hasMore || false);
        if (reset) setPage(1);
      }
    } catch (err) {
      setError('Failed to load products');
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, page, existingProductIds]);

  // Initial load
  useEffect(() => {
    loadExistingProducts();
  }, [loadExistingProducts]);

  // Load products after existing products are loaded
  useEffect(() => {
    if (existingProductIds.size >= 0) {
      loadProducts(true);
    }
  }, [existingProductIds, searchTerm]);

  // Handle search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Toggle product selection
  const toggleProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.hasOwnProperty(productId)) {
        const { [productId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: 0 };
    });
  };

  // Update quantity for a selected product
  const updateQuantity = (productId, quantity) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: Math.max(0, parseInt(quantity) || 0)
    }));
  };

  // Select all visible products (with 0 quantity by default)
  const selectAll = () => {
    setSelectedProducts(prev => {
      const newSelected = { ...prev };
      products.forEach(p => {
        if (!newSelected.hasOwnProperty(p.id)) {
          newSelected[p.id] = 0;
        }
      });
      return newSelected;
    });
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedProducts({});
  };

  // Get count of selected products
  const selectedCount = Object.keys(selectedProducts).length;

  // Add selected products to branch
  const handleAddProducts = async () => {
    if (selectedCount === 0) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // Convert to array of {productId, quantity} objects
      const productsToAdd = Object.entries(selectedProducts).map(([productId, quantity]) => ({
        productId: parseInt(productId),
        quantity: quantity || 0
      }));
      
      const response = await initializeBranchInventory(branchId, productsToAdd);
      
      if (response.success) {
        onSuccess?.(selectedCount);
        onClose();
      } else {
        setError(response.message || 'Failed to add products');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add products to branch');
      console.error('Error adding products:', err);
    } finally {
      setSaving(false);
    }
  };

  // Load more products
  const loadMore = () => {
    setPage(prev => prev + 1);
    loadProducts(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-theme-secondary rounded-xl shadow-2xl border border-theme">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-theme">
            <div>
              <h2 className="text-xl font-semibold text-theme-primary">
                Add Products to Branch
              </h2>
              <p className="text-sm text-theme-secondary mt-1">
                Select products from the catalog to add to <strong>{branchName}</strong>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="px-6 py-4 border-b border-theme">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-theme-tertiary" />
              <input
                type="text"
                placeholder="Search products by name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-theme-tertiary border border-theme rounded-lg text-theme-primary placeholder:text-theme-tertiary focus:outline-none focus:ring-2 focus:ring-accent-gold/50"
              />
            </div>
            
            {/* Selection info */}
            <div className="flex items-center justify-between mt-3">
              <span className="text-sm text-theme-secondary">
                {selectedCount} product{selectedCount !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-sm text-accent-gold hover:text-accent-gold/80 transition-colors"
                >
                  Select All Visible
                </button>
                {selectedCount > 0 && (
                  <button
                    onClick={clearSelection}
                    className="text-sm text-red-400 hover:text-red-300 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Products List */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {loading && products.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-accent-gold animate-spin" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-theme-tertiary mx-auto mb-3" />
                <p className="text-theme-secondary">
                  {searchTerm 
                    ? 'No products found matching your search'
                    : 'All products are already added to this branch'
                  }
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {products.map((product) => {
                    const isSelected = selectedProducts.hasOwnProperty(product.id);
                    return (
                      <div
                        key={product.id}
                        className={`
                          p-3 rounded-lg transition-all
                          ${isSelected
                            ? 'bg-accent-gold/20 border border-accent-gold/50'
                            : 'bg-theme-tertiary hover:bg-theme-tertiary/70 border border-transparent'
                          }
                        `}
                      >
                        <div 
                          className="flex items-center gap-4 cursor-pointer"
                          onClick={() => toggleProduct(product.id)}
                        >
                          {/* Checkbox */}
                          <div className={`
                            w-5 h-5 rounded flex items-center justify-center flex-shrink-0
                            ${isSelected
                              ? 'bg-accent-gold text-black'
                              : 'border-2 border-theme-tertiary'
                            }
                          `}>
                            {isSelected && (
                              <Check className="w-3.5 h-3.5" />
                            )}
                          </div>
                          
                          {/* Product Image */}
                          <div className="w-12 h-12 bg-theme-secondary rounded-lg overflow-hidden flex-shrink-0">
                            {product.images && product.images[0] ? (
                              <img
                                src={typeof product.images === 'string' 
                                  ? JSON.parse(product.images)[0] 
                                  : product.images[0]
                                }
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-6 h-6 text-theme-tertiary" />
                              </div>
                            )}
                          </div>
                          
                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-theme-primary truncate">
                              {product.name}
                            </h4>
                            <p className="text-sm text-theme-secondary truncate">
                              {product.product_code} • {product.brand_name || 'No brand'}
                            </p>
                          </div>
                          
                          {/* Price Info */}
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-medium text-theme-primary">
                              ₦{parseFloat(product.selling_price || 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-theme-secondary">
                              Global: {product.stock_quantity || 0}
                            </p>
                          </div>
                        </div>
                        
                        {/* Quantity Input - shown when selected */}
                        {isSelected && (
                          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-accent-gold/30">
                            <label className="text-sm text-theme-secondary">Initial Stock:</label>
                            <input
                              type="number"
                              min="0"
                              value={selectedProducts[product.id] || ''}
                              onChange={(e) => updateQuantity(product.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="0"
                              className="w-24 px-3 py-1.5 bg-theme-secondary border border-theme rounded-lg text-theme-primary text-center focus:outline-none focus:ring-2 focus:ring-accent-gold/50"
                            />
                            <span className="text-xs text-theme-tertiary">units</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Load More */}
                {hasMore && (
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="w-full mt-4 py-2 text-sm text-accent-gold hover:text-accent-gold/80 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Loading...' : 'Load More Products'}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-theme bg-theme-tertiary/30 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-theme-secondary hover:text-theme-primary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddProducts}
              disabled={selectedCount === 0 || saving}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-black font-medium rounded-lg hover:bg-accent-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add {selectedCount} Product{selectedCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProductToBranchModal;
